import React, { useState, useEffect } from 'react';
import { 
  Typography, Paper, CircularProgress, Box, Chip, 
  Collapse, IconButton, Alert, Grid, Card, CardContent, 
  Divider
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../utils/api'; // 引入API工具

function AutoRecordView() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [channels, setChannels] = useState([]);

  // 獲取規則列表
  useEffect(() => {
    document.title = '自動錄製規則';
    const fetchRules = async () => {
      try {
        // v2 的 GET /auto-record-rules 為公開端點
        const response = await api.get('/auto-record-rules');
        setRules(response.data || []);
        setError(null);
      } catch (err) {
        console.error('獲取自動錄製規則失敗:', err);
        setError('獲取自動錄製規則失敗，請稍後再試');
      } finally {
        setLoading(false);
      }
    };

    // 獲取頻道列表
    const fetchChannels = async () => {
      try {
        const response = await api.get('/channels');
        setChannels(response.data || []);
      } catch (err) {
        console.error('獲取頻道列表失敗:', err);
      }
    };

    fetchRules();
    fetchChannels();
  }, []);

  // 切換行的展開/收起
  const toggleRowExpansion = (id) => {
    setExpandedRows({
      ...expandedRows,
      [id]: !expandedRows[id]
    });
  };

  // 根據ID獲取頻道名稱
  const getChannelName = (id) => {
    const channel = channels.find(ch => ch.id === id);
    return channel ? channel.name : id;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        自動錄製規則（唯讀模式）
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        您正在查看自動錄製規則列表。這些規則用於自動匹配和錄製廣播節目。
      </Alert>

      {rules.length === 0 ? (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography align="center">目前沒有自動錄製規則</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {rules.map((rule) => (
            <Grid item xs={12} key={rule.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {rule.name}
                      {' '}
                      <Chip 
                        size="small"
                        color={rule.is_active ? "success" : "default"}
                        label={rule.is_active ? "啟用中" : "已停用"}
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => toggleRowExpansion(rule.id)}
                    >
                      {expandedRows[rule.id] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      關鍵字:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {rule.keywords.map((keyword, idx) => (
                        <Chip 
                          key={idx} 
                          label={keyword} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      頻道:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {rule.channels.map((channelId, idx) => (
                        <Chip 
                          key={idx} 
                          label={getChannelName(channelId)} 
                          size="small" 
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Collapse in={expandedRows[rule.id]} timeout="auto" unmountOnExit>
                    <Divider sx={{ my: 2 }} />

                    {rule.exclude_keywords && rule.exclude_keywords.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                          排除關鍵字:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {rule.exclude_keywords.map((keyword, idx) => (
                            <Chip 
                              key={idx} 
                              label={keyword} 
                              size="small" 
                              color="error" 
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        建立時間: {new Date(rule.created_at).toLocaleString()}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        更新時間: {new Date(rule.updated_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
}

export default AutoRecordView;